import React, { useEffect, useRef } from 'react';
import './AudioVisualizer.css';

function applyAlphaToRgb(rgbString, alpha) {
  const match = rgbString.match(/\d+/g);
  if (!match || match.length !== 3) return rgbString;
  const [r, g, b] = match;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function AudioVisualizer({ stream, voiceConfiguration, shape }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!stream) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const topBinsDiscarded = bufferLength * 0.8;

    const strokeColor =
      voiceConfiguration?.web_voice_widget_configuration
        ?.chat_audio_visualizer_stroke_color ?? 'rgb(0,0,0)';
    const innerColor =
      voiceConfiguration?.web_voice_widget_configuration
        ?.chat_audio_visualizer_inner_color ?? '#fff';
    const middleColor =
      voiceConfiguration?.web_voice_widget_configuration
        ?.chat_audio_visualizer_middle_color ?? '#66f';
    const outerColor =
      voiceConfiguration?.web_voice_widget_configuration
        ?.chat_audio_visualizer_outer_color ?? '#003';

    const numRings = 3;
    const innerRadius = Math.min(centerX, centerY) * 0.2;
    const bufferSpace = Math.min(centerX, centerY) * 0.5;
    const maxRingExpansion = Math.min(centerX, centerY) * 0.2;
    const binsPerRing = Math.floor(
      (bufferLength - topBinsDiscarded) / numRings
    );

    function drawCenterGlow() {
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        innerRadius * 0.1,
        centerX,
        centerY,
        innerRadius
      );
      gradient.addColorStop(0, innerColor);
      gradient.addColorStop(0.5, middleColor);
      gradient.addColorStop(1, outerColor);

      ctx.save();
      ctx.shadowColor = outerColor;
      ctx.shadowBlur = 5;
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    function drawCircularRings(time) {
      for (let ring = 0; ring < numRings; ring++) {
        let sum = 0;
        for (let i = ring * binsPerRing; i < (ring + 1) * binsPerRing; i++) {
          sum += dataArray[i];
        }

        const avg = sum / binsPerRing;
        const expansion = (avg / 255) * maxRingExpansion;
        const baseRingRadius =
          innerRadius +
          ((ring + 1) *
            (Math.min(centerX, centerY) - innerRadius - bufferSpace)) /
            numRings;
        const waveAmplitude = (avg / 255) * 10;
        const numPoints = 100;
        const angleStep = (Math.PI * 2) / numPoints;

        const dynamicStrokeColor = strokeColor.startsWith('rgb')
          ? applyAlphaToRgb(strokeColor, avg / 255)
          : strokeColor;

        ctx.strokeStyle = dynamicStrokeColor;
        ctx.lineWidth = 3;
        ctx.beginPath();
        for (let i = 0; i <= numPoints; i++) {
          const angle = i * angleStep;
          const waveOffset =
            Math.sin(angle * 3 + time + (ring * Math.PI) / numRings) *
            waveAmplitude;
          const radius = baseRingRadius + expansion + waveOffset;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }

    function drawBars(time) {
      const radius = innerRadius + 10; // Just outside the center glow
      const barLength = (Math.min(centerX, centerY) - radius) * 0.9;
      const angleStep = (Math.PI * 2) / bufferLength;

      for (let i = 0; i < bufferLength; i++) {
        const value = dataArray[i];
        const angle = i * angleStep;
        const scaledLength = (value / 255) * barLength;

        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + scaledLength);
        const y2 = centerY + Math.sin(angle) * (radius + scaledLength);

        ctx.strokeStyle = applyAlphaToRgb(strokeColor, value / 255);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }

    function drawSpiral(time) {
      ctx.beginPath();
      const angleStep = 0.1;
      for (let i = 0; i < bufferLength; i++) {
        const value = dataArray[i];
        const angle = i * angleStep;
        const radius = (value / 255) * 50 + i * 0.5;
        const x = centerX + Math.cos(angle + time * 0.1) * radius;
        const y = centerY + Math.sin(angle + time * 0.1) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    function drawWaveform(time) {
      analyser.getByteTimeDomainData(dataArray);
      ctx.lineWidth = 2;
      ctx.strokeStyle = strokeColor;
      ctx.beginPath();

      const sliceWidth = width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0; // Normalize
        const y = (v * height) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.lineTo(width, height / 2);
      ctx.stroke();
    }

    function drawStarburst(time) {
      const angleStep = (Math.PI * 2) / bufferLength;
      ctx.beginPath();
      for (let i = 0; i < bufferLength; i++) {
        const value = dataArray[i];
        const radius =
          innerRadius +
          (value / 255) * (Math.min(centerX, centerY) - innerRadius);
        const angle = i * angleStep + time * 0.01;

        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    function drawHexagrid(time) {
      const maxRings = 5;
      const hexSize = 10;
      const spacing = hexSize * 2; // distance between rings

      let index = 0;

      for (let ring = 1; ring <= maxRings; ring++) {
        const radius = innerRadius + ring * spacing;
        const hexesInRing = Math.ceil((2 * Math.PI * radius) / (hexSize * 2.2));

        for (let i = 0; i < hexesInRing; i++) {
          const angle = (i / hexesInRing) * Math.PI * 2 + time * 0.002;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;

          const value = dataArray[index % dataArray.length];
          const pulse = 1 + (value / 255) * 0.4;

          drawHexagon(
            x,
            y,
            hexSize * pulse,
            applyAlphaToRgb(strokeColor, value / 255)
          );
          index++;
        }
      }
    }

    function drawHexagon(x, y, radius, color) {
      const angleStep = (Math.PI * 2) / 6;
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const angle = i * angleStep;
        const px = x + Math.cos(angle) * radius;
        const py = y + Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    function draw() {
      requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const time = Date.now() * 0.005;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.fillRect(0, 0, width, height);

      drawCenterGlow();

      if (shape === 'circle') drawCircularRings(time);
      else if (shape === 'bars') drawBars(time);
      else if (shape === 'spiral') drawSpiral(time);
      else if (shape === 'waveform') drawWaveform(time);
      else if (shape === 'starburst') drawStarburst(time);
      else if (shape === 'hexagrid') drawHexagrid(time);
    }

    draw();

    return () => {
      audioContext.close();
    };
  }, [stream, voiceConfiguration, shape]);

  return (
    <div className="audio-visualizer-container">
      <canvas ref={canvasRef} width={200} height={200} />
    </div>
  );
}
